import { Event, Person } from '@mui/icons-material';
import { Box } from '@mui/material';
import moment from 'moment';
import * as React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useLoggedInEmployeeId } from '~/components/Auth';
import {
  EmployeeCalendar,
  useEmployeeCalendarPDFExport,
} from '~/components/EmployeeCalendar';
import {
  AnalyticsReportEmployeeScope,
  AnalyticsReportScope,
  AnalyticsReportTypes,
  useVacationsReportData,
} from '~/data/reports';
import {
  ReportDataExportCustom,
  ReportDatasetLayout,
  ReportDatasetRowCount,
  ReportDatasetSelector,
  ReportDateRangeSelector,
  ReportDateRangeSelectorMode,
  ReportViewLayout,
  useReportDataset,
  useReportNavigateToView,
} from '~/scenes/PortalModules/Analytics/common';
import { ShowEmployeesModeSelector } from '~/scenes/PortalModules/Analytics/People/AbsenceCalendarReport/ShowEmployeesModeSelector';
import { useVacationsCalendarReport } from '~/scenes/PortalModules/Analytics/People/VacationCalendarReport/useVacationCalendarReport';

export const VacationCalendarReport = () => {
  const { t } = useTranslation();

  const { goToEmployeeBasicDataView } = useReportNavigateToView();

  const loggedInEmployeeId = useLoggedInEmployeeId();

  const defaultReportSelection = useMemo(() => {
    return {
      reportType: {
        type: AnalyticsReportTypes.Unit,
        employeeId: loggedInEmployeeId,
        scope: AnalyticsReportScope.Direct,
      },
      params: {
        startDate: moment().subtract(1, 'month').toDate(),
        endDate: moment().add(2, 'months').toDate(),
        empScope: AnalyticsReportEmployeeScope.AllEmployees,
      },
    };
  }, [loggedInEmployeeId]);

  const reportDatasetProps = useReportDataset({
    selection: defaultReportSelection,
    config: {
      options: {
        types: [AnalyticsReportTypes.Employee, AnalyticsReportTypes.Unit],
      },
    },
  });

  const { reportRange } = useMemo(() => {
    const reportRange = {
      startDate:
        reportDatasetProps.reportSelection?.params?.startDate ??
        defaultReportSelection.params.startDate,
      endDate:
        reportDatasetProps.reportSelection?.params?.endDate ??
        defaultReportSelection.params.endDate,
    };

    return {
      reportRange,
    };
  }, [reportDatasetProps, defaultReportSelection]);

  const {
    data,
    isLoading: isLoadingReportData,
    failedToLoad,
  } = useVacationsReportData(reportDatasetProps.reportSelection);

  const {
    showEmployeesModeSelector,
    counts,
    calendarProps,
    isLoading: isLoadingPublicHolidays,
  } = useVacationsCalendarReport({
    data,
    ...reportRange,
  });

  const { pdfRef, onCalendarPDFExport } = useEmployeeCalendarPDFExport({
    title: t('laVacationCalendar'),
    rowsCount: calendarProps.listEmployees.length,
  });

  return (
    <ReportViewLayout
      heading={t('laVacationCalendar')}
      slots={{
        toolbar: {
          main: (
            <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <ReportDatasetLayout showHeading={true}>
                <ReportDatasetSelector
                  {...reportDatasetProps}
                  isLoading={isLoadingReportData || isLoadingPublicHolidays}
                  failedToLoad={failedToLoad}
                  renderCurrentParams={({ params }) => {
                    return (
                      <>{`${moment(params?.startDate).format('L')} - ${moment(
                        params?.endDate,
                      ).format('L')}`}</>
                    );
                  }}
                  renderParamsFields={({ params, onChange }) => {
                    return (
                      <ReportDateRangeSelector
                        startDate={params.startDate}
                        endDate={params.endDate}
                        onRangeChange={(startDate, endDate) =>
                          onChange({
                            ...params,
                            startDate: startDate ?? params.startDate,
                            endDate: endDate ?? params.endDate,
                          })
                        }
                        mode={ReportDateRangeSelectorMode.DateRange}
                      />
                    );
                  }}
                />
                <ReportDatasetRowCount
                  count={counts.employees}
                  description={t('laTotalRecords')}
                  icon={<Person />}
                />
                <ReportDatasetRowCount
                  count={counts.absences}
                  description={t('laAbsences')}
                  icon={<Event />}
                />
              </ReportDatasetLayout>
              <ShowEmployeesModeSelector {...showEmployeesModeSelector} />
            </Box>
          ),
          actions: (
            <ReportDataExportCustom
              onCustomPDFExport={onCalendarPDFExport}
              isLoading={isLoadingReportData || isLoadingPublicHolidays}
            />
          ),
        },
      }}
    >
      <EmployeeCalendar
        ref={pdfRef}
        containerWidth={500}
        startDate={reportRange.startDate}
        endDate={reportRange.endDate}
        {...calendarProps}
        onEmployeeClick={goToEmployeeBasicDataView}
      />
    </ReportViewLayout>
  );
};
